// useCreateTypeForm.js

import { useCallback, useEffect, useMemo, useState } from 'react';

// Services
import { createBaseNodeType } from 'services/nodetypes/NodeTypeWriteService';
import useBaseAccess from 'hooks/useBaseAccess';

// Utilities
import {
  formatDatabaseTypeLabel,
  getDatabaseTypeLabelFormatError,
} from 'utilities/databaseTypeLabels';
import { ViewType } from 'utilities/NodeType';
import { pluralizeTitle } from 'utilities/Languages';

/**
 * useCreateTypeForm
 *
 * Custom hook for managing the state and logic of the CreateTypeSection in the AddModal component.
 * It handles form state, validation, and submission for creating a new node type within a space.
 * 
 * @param {boolean} isOpen - Whether the Create Type form is open.
 * @param {Array} typeDefs - The list of existing type definitions for validation against duplicates.
 * @param {string} userKey - The key of the current user, used for auditing purposes when creating a type.
 * @param {string} baseKey - The key of the current base where the type will be created.
 * @param {function} onTypeSelect - Callback function to call with the new type key after successful creation.
 * @param {function} onClose - Callback function to call when the form is closed, for cleanup.
 *
 * @returns {Object} An object containing state and handlers for managing the Create Type form.
 */
export function useCreateTypeForm({
  isOpen,
  typeDefs,
  excludeTypeKey,
  userKey,
  baseKey,
  anchorNodeKey,
  onTypeSelect,
  onClose,
}) {
  const { can, isRoleLoading } = useBaseAccess();

  // ----- STATE ----- //

  const [label, setLabel] = useState('');
  const [labelPlural, setLabelPlural] = useState('');
  const [description, setDescription] = useState('');
  const [iconKey, setIconKey] = useState('deployed_code');
  const [defaultView, setDefaultView] = useState(ViewType.LIST);
  const [pluralTouched, setPluralTouched] = useState(false);
  const [typeError, setTypeError] = useState('');
  const [isSavingType, setIsSavingType] = useState(false);

  // ----- EFFECTS ----- //
  
  // Reset form state when the form is opened
  useEffect(() => {
    if (!isOpen) return;

    setLabel('');
    setLabelPlural('');
    setDescription('');
    setIconKey('deployed_code');
    setDefaultView(ViewType.LIST);
    setPluralTouched(false);
    setTypeError('');
    setIsSavingType(false);
  }, [isOpen]);

  // Auto-generate the plural label based on the singular label, unless the plural field 
  // has been manually touched
  useEffect(() => {
    if (pluralTouched) return;
    const cleaned = String(label || '').trim();
    if (!cleaned) {
      setLabelPlural('');
      return;
    }
    setLabelPlural(pluralizeTitle(cleaned));
  }, [label, pluralTouched]);

  // ----- MEMOIZED VALUES ----- //
  
  // Check for duplicate labels against existing type definitions
  const duplicateState = useMemo(() => {
    const normalizedLabel = String(label || '').trim().toLowerCase();
    const normalizedPlural = String(labelPlural || '').trim().toLowerCase();
    const normalizedExcludedTypeKey = String(excludeTypeKey || '').trim();
    const existingNames = new Set();

    typeDefs.forEach((typeDef) => {
      if (!typeDef) return;

      const candidateKey = String(typeDef?.key || '').trim();
      if (candidateKey && candidateKey === normalizedExcludedTypeKey) return;

      const singular = String(typeDef.label || '').trim().toLowerCase();
      const plural = String(typeDef.labelPlural || '').trim().toLowerCase();

      if (singular) existingNames.add(singular);
      if (plural) existingNames.add(plural);
    });

    const labelFormatError = getDatabaseTypeLabelFormatError(label);
    const pluralFormatError = getDatabaseTypeLabelFormatError(labelPlural);

    return {
      labelError: labelFormatError
        || (normalizedLabel && existingNames.has(normalizedLabel)
          ? 'That label already exists in your available type set (system or base).'
          : ''),
      pluralError: pluralFormatError
        || (normalizedPlural && existingNames.has(normalizedPlural)
          ? 'That plural label already exists in your available type set (system or base).'
          : ''),
    };
  }, [excludeTypeKey, label, labelPlural, typeDefs]);

  // Determine if the form can be submitted based on validation and saving state
  const canSaveType = useMemo(() => {
    return Boolean(label.trim())
      && !duplicateState.labelError
      && !duplicateState.pluralError
      && !isRoleLoading
      && can('types.create')
      && !isSavingType;
  }, [can, duplicateState.labelError, duplicateState.pluralError, isRoleLoading, isSavingType, label]);

  // ----- HANDLERS ----- //

  /**
   * Handles the creation of a new node type when the form is submitted. It performs validation,
   * checks for duplicates, and calls the createBaseNodeType service function. If creation is
   * successful, it calls the onTypeSelect callback with the new type's key and closes the form.
   * 
   * @returns {Promise<void>} A promise that resolves when the creation process is complete.
   */
  const handleCreateType = useCallback(async () => {

    // Ensure the label is not empty
    if (!label.trim()) {
      setTypeError('Please enter a label.');
      return;
    }

    if (!can('types.create')) {
      setTypeError('You do not have permission to create types in this space.');
      return;
    }

    // Check for duplicate label errors before attempting to create the type
    if (duplicateState.labelError || duplicateState.pluralError) {
      setTypeError(duplicateState.labelError || duplicateState.pluralError);
      return;
    }

    // Set saving state to true to disable the form and provide feedback
    setIsSavingType(true);

    // Clear any existing type errors before attempting to create
    setTypeError('');

    const formattedLabel = formatDatabaseTypeLabel(label);
    const formattedLabelPlural = formatDatabaseTypeLabel(labelPlural) || pluralizeTitle(formattedLabel);

    const formatError = getDatabaseTypeLabelFormatError(formattedLabel)
      || getDatabaseTypeLabelFormatError(formattedLabelPlural);
    if (formatError) {
      setTypeError(formatError);
      setIsSavingType(false);
      return;
    }

    // Attempt to create the new node type using the service function
    try {
      const created = await createBaseNodeType({
        label: formattedLabel,
        labelPlural: formattedLabelPlural,
        description,
        iconKey,
        defaultView,
        userKey,
        baseKey,
        anchorNodeKey,
      });

      // If creation was successful and we have a new type key, call the onTypeSelect callback
      if (created?.key && typeof onTypeSelect === 'function') {
        onTypeSelect(created.key);
      }
      onClose?.();
    } catch (error) {
      setTypeError(error?.message || 'Failed to create type.');
    } finally {
      setIsSavingType(false);
    }
  }, [
    duplicateState.labelError,
    duplicateState.pluralError,
    iconKey,
    can,
    defaultView,
    description,
    label,
    labelPlural,
    onClose,
    onTypeSelect,
    userKey,
    baseKey,
    anchorNodeKey,
  ]);

  // While saving, hide live duplicate checks. The new type can appear in `typeDefs`
  // via realtime before we exclude it / advance the wizard, which briefly flashes
  // a false "already exists" error.
  const labelHelperText = typeError || (isSavingType ? '' : duplicateState.labelError) || '';

  // Plural helper text to show the duplicate plural label error, if present
  const pluralHelperText = isSavingType ? '' : (duplicateState.pluralError || '');

  // Handler for when the plural label field changes, which marks the plural as manually touched to
  // prevent auto-generation from the singular label
  const handleLabelPluralChange = useCallback((value) => {
    setPluralTouched(true);
    setLabelPlural(value);
  }, []);

  const handleLabelBlur = useCallback(() => {
    setLabel((current) => formatDatabaseTypeLabel(current));
  }, []);

  const handleLabelPluralBlur = useCallback(() => {
    setLabelPlural((current) => formatDatabaseTypeLabel(current));
  }, []);

  return {
    label,
    setLabel,
    labelPlural,
    description,
    setDescription,
    iconKey,
    setIconKey,
    defaultView,
    setDefaultView,
    isSavingType,
    canSaveType,
    labelHelperText,
    pluralHelperText,
    handleLabelPluralChange,
    handleLabelBlur,
    handleLabelPluralBlur,
    handleCreateType,
  };
}
